/**
 * Tray ("Ready trays", rules v2) view helpers, free of Phaser so they are unit-tested in node: which
 * pieces belong to a tray, what each tray shows, and which pop of a batch is a tray's. Core decides
 * every rule; these only read its compiled level, state and events.
 *
 * Piece order (core CompiledLevel): board tiles, crates, conveyor entries, dispenser items, then
 * every tray's three pieces (trayStart[k], +1, +2). A tray is one tile-sized cell at its pieces'
 * shared x, y, layer, holding its item three times; only its first piece is a tap target, and the
 * tap pops all three at once without the bag (event `tray`, then the pop as a bag pop resolves it).
 */
import { PIECE_BOARD, type CompiledLevel, type Round, type RoundEvent } from '@mp/core';

/** Tray index of piece i, or -1 (any piece of the tray, the first or its other two). */
export function trayOf(L: CompiledLevel, i: number): number {
  return L.nTrays ? (L.trayOf[i] ?? -1) : -1;
}

/** The tray's first piece: the one a tap on any part of the tray takes. */
export const trayFirst = (L: CompiledLevel, k: number): number => L.trayStart[k]!;

/** The tray's three pieces, ascending. */
export function trayPieces(L: CompiledLevel, k: number): [number, number, number] {
  const a = L.trayStart[k]!;
  return [a, a + 1, a + 2];
}

export interface TrayState {
  /** still on the board (its three pieces leave together) */
  shown: boolean;
  /** a tile lies over it: dimmed like a covered tile, not tappable */
  covered: boolean;
  /** the item it holds three times (an assist swap relabels all three together) */
  item: number;
}

export function trayState(r: Round, k: number): TrayState {
  const i = r.L.trayStart[k]!;
  const shown = r.piece[i] === PIECE_BOARD;
  return { shown, covered: shown && r.cover[i]! > 0, item: r.item[i]! };
}

/** Trays still on the board. */
export function traysLeft(r: Round): number {
  let n = 0;
  for (let k = 0; k < r.L.nTrays; k++) if (trayState(r, k).shown) n++;
  return n;
}

/** The tray a `popped` event's pieces belong to (a tray pop never went through the bag), or -1. */
export function trayPopOf(L: CompiledLevel, tiles: readonly number[]): number {
  const t = tiles[0];
  return t === undefined ? -1 : trayOf(L, t);
}

/** The `tray` events of a batch (at most one: a tap pops one tray). */
export function trayEvents(events: readonly RoundEvent[]): Extract<RoundEvent, { type: 'tray' }>[] {
  return events.filter((e): e is Extract<RoundEvent, { type: 'tray' }> => e.type === 'tray');
}
